import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClone,
  faMagnifyingGlass,
  faStar,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import flashcardService, { getErrorMessage } from '../../services/flashcardService';
import DeckFilters, { findLevelOption, findRatingOption } from './DeckFilters';
import DecksGrid from './DecksGrid';
import StudySession from './StudySession';
import DeckFormModal from './DeckFormModal';
import StatsModal from './StatsModal';
import ReviewModal from './ReviewModal';
import UpgradePrompt from './UpgradePrompt';
import './Flashcards.css';

const PAGE_SIZE = 12;

/**
 * Tabs are a UI concept, not a 1:1 mapping of the server's `scope`. Each tab
 * owns the exact query params it sends, so "Recent" can reuse `scope=All`
 * while switching the sort and adding `recentOnly`.
 */
const TABS = [
  { key: 'All', label: 'All Decks', params: { scope: 'All' } },
  { key: 'Mine', label: 'My Decks', params: { scope: 'Mine' } },
  { key: 'Public', label: 'Public Decks', params: { scope: 'Public' } },
  {
    key: 'Recent',
    label: 'Recent',
    params: { scope: 'All', sortBy: 'Recent', recentOnly: true },
  },
];

const getTab = (key) => TABS.find((t) => t.key === key) || TABS[0];

// Safe fallback so the page still renders (browse-only) if entitlement fails.
const FALLBACK_ENTITLEMENT = {
  canCreate: false,
  canReview: false,
  studyDailyLimit: null,
  studyRemainingToday: null,
  requiredPlans: ['Standard', 'Premium'],
};

const FlashcardsPage = () => {
  const [entitlement, setEntitlement] = useState(null);

  const [view, setView] = useState('decks'); // 'decks' | 'study'
  const [activeTab, setActiveTab] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [difficulty, setDifficulty] = useState(null); // null | 1 | 2 | 3
  const [minRating, setMinRating] = useState(null); // null | 1 | 6 | 7 | 8 | 9
  const [maxRating, setMaxRating] = useState(null); // null | 6 (only set for the "1 to 5" bucket)
  const [page, setPage] = useState(1);

  const [decks, setDecks] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loadingDecks, setLoadingDecks] = useState(true);
  const [decksError, setDecksError] = useState(false);

  const [activeStudyDeck, setActiveStudyDeck] = useState(null);
  const [deckFormState, setDeckFormState] = useState({ open: false, deck: null });
  const [statsDeck, setStatsDeck] = useState(null);
  const [reviewDeck, setReviewDeck] = useState(null);
  const [lockedPrompt, setLockedPrompt] = useState(null); // variant string or null
  const [toast, setToast] = useState(null);

  // Transient success banner (e.g. "Published as version 2"), auto-dismissed.
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Fetch entitlement once per page mount (never cached across loads).
  useEffect(() => {
    let active = true;
    flashcardService
      .getEntitlement()
      .then((data) => {
        if (active) setEntitlement(data || FALLBACK_ENTITLEMENT);
      })
      .catch(() => {
        if (active) setEntitlement(FALLBACK_ENTITLEMENT);
      });
    return () => {
      active = false;
    };
  }, []);

  // Debounce the search box -> server-side `name` param.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchDecks = useCallback(() => {
    setLoadingDecks(true);
    setDecksError(false);
    flashcardService
      .getDecks({
        ...getTab(activeTab).params,
        name: debouncedSearch || undefined,
        // null/undefined params are dropped by createQueryString, which is what
        // "Any level" / "Any rating" need — `minRating` excludes unrated decks,
        // so it must be absent rather than sent as a floor of 1. `maxRating` is
        // exclusive and only set for the "1 to 5" bucket; every other option
        // leaves it null so it's omitted the same way.
        difficulty,
        minRating,
        maxRating,
        pageNumber: page,
        pageSize: PAGE_SIZE,
      })
      .then((res) => {
        setDecks(res?.items || []);
        setPagination(
          res
            ? {
                pageNumber: res.pageNumber,
                pageSize: res.pageSize,
                totalCount: res.totalCount,
                totalPages: res.totalPages,
                hasPreviousPage: res.hasPreviousPage,
                hasNextPage: res.hasNextPage,
              }
            : null
        );
      })
      .catch(() => setDecksError(true))
      .finally(() => setLoadingDecks(false));
  }, [activeTab, debouncedSearch, difficulty, minRating, maxRating, page]);

  useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  // ----- Preference (favorite / bookmark) optimistic toggles ---------------
  const patchDeckLocal = (deckId, patch) => {
    setDecks((prev) =>
      prev.map((d) => (d.id === deckId ? { ...d, ...patch } : d))
    );
  };

  const toggleFavorite = (deck) => {
    const next = !deck.isFavorite;
    patchDeckLocal(deck.id, { isFavorite: next });
    flashcardService
      .setPreference(deck.id, { isFavorite: next, inStudyList: null })
      .catch(() => patchDeckLocal(deck.id, { isFavorite: deck.isFavorite }));
  };

  const toggleBookmark = (deck) => {
    const next = !deck.inStudyList;
    patchDeckLocal(deck.id, { inStudyList: next });
    flashcardService
      .setPreference(deck.id, { isFavorite: null, inStudyList: next })
      .catch(() => patchDeckLocal(deck.id, { inStudyList: deck.inStudyList }));
  };

  // ----- Study lifecycle ---------------------------------------------------
  const startStudy = (deck) => {
    setActiveStudyDeck(deck);
    setView('study');
  };

  const exitStudy = () => {
    setView('decks');
    setActiveStudyDeck(null);
    fetchDecks();
  };

  // ----- Deck CRUD ---------------------------------------------------------
  const openNewDeck = () => {
    if (entitlement && !entitlement.canCreate) {
      setLockedPrompt('create');
      return;
    }
    setDeckFormState({ open: true, deck: null });
  };

  const editDeck = (deck) => setDeckFormState({ open: true, deck });

  const deleteDeck = async (deck) => {
    if (!window.confirm(`Delete "${deck.name}"? This can't be undone.`)) return;
    try {
      await flashcardService.deleteDeck(deck.id);
      fetchDecks();
    } catch {
      window.alert('Could not delete the deck. Please try again.');
    }
  };

  const onDeckSaved = () => {
    setDeckFormState({ open: false, deck: null });
    fetchDecks();
  };

  // ----- Publish-as-snapshot -------------------------------------------------
  const publishDeck = async (deck) => {
    const count = deck.cardCount || 0;
    const confirmMsg = `Publish this deck? A public snapshot of its current ${count} cards will be created. Published versions cannot be edited or deleted.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await flashcardService.publishDeck(deck.id);
      const version = res && (res.version ?? res.publishedVersion);
      setToast(version != null ? `Published as version ${version}` : 'Deck published.');
      fetchDecks();
    } catch (err) {
      window.alert(getErrorMessage(err, 'Could not publish this deck. Please try again.'));
    }
  };

  const unlistDeck = async (deck) => {
    if (!deck.publishedDeckId) return;
    if (!window.confirm('Remove from public browsing? Users already studying it keep access.')) {
      return;
    }
    try {
      await flashcardService.unlistDeck(deck.publishedDeckId);
      setToast('Deck removed from public browsing.');
      fetchDecks();
    } catch (err) {
      window.alert(getErrorMessage(err, 'Could not unlist this deck. Please try again.'));
    }
  };

  const relistDeck = async (deck) => {
    if (!deck.publishedDeckId) return;
    try {
      await flashcardService.relistDeck(deck.publishedDeckId);
      setToast('Deck relisted for public browsing.');
      fetchDecks();
    } catch (err) {
      window.alert(getErrorMessage(err, 'Could not relist this deck. Please try again.'));
    }
  };

  // A newer published version exists — fetch it and open it exactly like any
  // other public deck (no dedicated "switch" API, per contract).
  const switchToNewerVersion = async (deck) => {
    const targetId = deck.latestVersionDeckId;
    if (!targetId) return;
    const confirmMsg =
      'Switching starts the new version with a fresh study schedule — your progress on this version is kept but not carried over.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const newDeck = await flashcardService.getDeck(targetId);
      setActiveStudyDeck(newDeck);
      setView('study');
    } catch {
      window.alert('Could not load the newest version of this deck. Please try again.');
    }
  };

  // ----- Reviews -----------------------------------------------------------
  const openReview = (deck) => setReviewDeck(deck);
  const openLockedReview = () => setLockedPrompt('review');
  const onReviewSaved = () => {
    setReviewDeck(null);
    fetchDecks();
  };

  const deckCardHandlers = {
    onStartStudy: startStudy,
    onOpenStats: (deck) => setStatsDeck(deck),
    onOpenReview: openReview,
    onLockedReview: openLockedReview,
    onEdit: editDeck,
    onDelete: deleteDeck,
    onToggleFavorite: toggleFavorite,
    onToggleBookmark: toggleBookmark,
    onPublish: publishDeck,
    onUnlist: unlistDeck,
    onRelist: relistDeck,
    onSwitchVersion: switchToNewerVersion,
  };

  // Any change to what's being browsed sends the user back to page 1.
  const changeTab = (nextTab) => {
    setActiveTab(nextTab);
    setPage(1);
  };

  const changeDifficulty = (value) => {
    setDifficulty(value);
    setPage(1);
  };

  const changeRating = (nextMinRating, nextMaxRating) => {
    setMinRating(nextMinRating);
    setMaxRating(nextMaxRating);
    setPage(1);
  };

  const clearFilters = () => {
    setDifficulty(null);
    setMinRating(null);
    setMaxRating(null);
    setPage(1);
  };

  // ----- Study view --------------------------------------------------------
  if (view === 'study' && activeStudyDeck) {
    return (
      <StudySession
        key={activeStudyDeck.id}
        deck={activeStudyDeck}
        entitlement={entitlement || FALLBACK_ENTITLEMENT}
        onExit={exitStudy}
        onSwitchVersion={() => switchToNewerVersion(activeStudyDeck)}
      />
    );
  }

  const totalCount = pagination?.totalCount;
  const filtersActive = difficulty !== null || minRating !== null;
  const levelChip = findLevelOption(difficulty);
  const ratingChip = findRatingOption(minRating, maxRating);

  return (
    <div className="fc-page">
      <div className="fc-header">
        <h1 className="fc-header-title">
          <FontAwesomeIcon icon={faClone} className="fc-header-icon" />
          Flashcards
        </h1>
        <p className="fc-header-subtitle">
          Study with spaced repetition to master your subjects.
        </p>
      </div>

      <div className="fc-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`fc-tab ${activeTab === tab.key ? 'fc-tab-active' : ''}`}
            onClick={() => changeTab(tab.key)}
            aria-pressed={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="fc-controls">
        <div className="fc-controls-row">
          <div className="fc-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="fc-search-icon" />
            <input
              className="fc-search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search decks by name or description…"
              aria-label="Search decks"
            />
            {searchInput && (
              <button
                type="button"
                className="fc-search-clear"
                onClick={() => setSearchInput('')}
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          <DeckFilters
            difficulty={difficulty}
            minRating={minRating}
            maxRating={maxRating}
            onChangeDifficulty={changeDifficulty}
            onChangeRating={changeRating}
            onClear={clearFilters}
          />
        </div>

        {filtersActive && (
          <div className="fc-active-filters">
            {difficulty !== null && (
              <button
                type="button"
                className="fc-chip"
                onClick={() => changeDifficulty(null)}
                aria-label={`Remove level filter: ${levelChip.chipLabel}`}
              >
                <span className={`fc-filter-dot ${levelChip.dotClass}`} />
                {levelChip.chipLabel}
                <FontAwesomeIcon icon={faTimes} className="fc-chip-x" />
              </button>
            )}
            {minRating !== null && (
              <button
                type="button"
                className="fc-chip"
                onClick={() => changeRating(null, null)}
                aria-label={`Remove rating filter: ${ratingChip.chipLabel}`}
              >
                <FontAwesomeIcon icon={faStar} className="fc-chip-star" />
                {ratingChip.chipLabel}
                <FontAwesomeIcon icon={faTimes} className="fc-chip-x" />
              </button>
            )}
            <button type="button" className="fc-chip-clear" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </div>

      {!loadingDecks && !decksError && totalCount !== undefined && (
        <div className="fc-result-count">
          {totalCount} {totalCount === 1 ? 'deck' : 'decks'} found
        </div>
      )}

      <DecksGrid
        decks={decks}
        entitlement={entitlement || FALLBACK_ENTITLEMENT}
        scope={getTab(activeTab).params.scope}
        tab={activeTab}
        search={debouncedSearch}
        filtersActive={filtersActive}
        onClearFilters={clearFilters}
        loading={loadingDecks || entitlement === null}
        error={decksError}
        pagination={pagination}
        onPageChange={setPage}
        onRetry={fetchDecks}
        onNewDeck={openNewDeck}
        deckCardHandlers={deckCardHandlers}
      />

      {deckFormState.open && (
        <DeckFormModal
          deck={deckFormState.deck}
          entitlement={entitlement || FALLBACK_ENTITLEMENT}
          onClose={() => setDeckFormState({ open: false, deck: null })}
          onSaved={onDeckSaved}
        />
      )}

      {statsDeck && (
        <StatsModal deck={statsDeck} onClose={() => setStatsDeck(null)} />
      )}

      {reviewDeck && (
        <ReviewModal
          deck={reviewDeck}
          entitlement={entitlement || FALLBACK_ENTITLEMENT}
          onClose={() => setReviewDeck(null)}
          onSaved={onReviewSaved}
        />
      )}

      {lockedPrompt && (
        <div className="fc-modal-overlay" onClick={() => setLockedPrompt(null)}>
          <div
            className="fc-modal fc-modal-sm"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="fc-modal-body">
              <UpgradePrompt
                variant={lockedPrompt}
                requiredPlans={(entitlement || FALLBACK_ENTITLEMENT).requiredPlans}
                onDismiss={() => setLockedPrompt(null)}
                dismissLabel="Not now"
              />
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fc-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default FlashcardsPage;
